import get from 'lodash/get';
import isBoolean from 'lodash/isBoolean';
import {useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import Box from '@mui/material/Box';

import AddressField from './AddressField';
import BooleanField from './BooleanField';
import ChoiceField from './ChoiceField';
import ColorPicker from './ColorPicker';
import CurrencyField from './CurrencyField';
import CustomInputLabel from './CustomInputLabel';
import CustomNumberField from './CustomNumberField';
import CustomSelect from './CustomSelect';
import CustomSliderField from './CustomSliderField';
import CustomTextField from './CustomTextField';
import DateField from './DateField';
import DateRangePicker from './DateRangeFilter';
import DayPicker from './DayPicker';
import FindCaseInput from '../cases/FindCaseInput';
import FindPatientForm from '../patients/FindPatientForm';
import FindPayerInput from '../payers/FindPayerInput';
import FindReferrerInput from '../referrers/FindReferrerInput';
import FindReferrerCompanyInput from '../referrers/FindReferrerCompanyInput';
import HtmlEditor from './HtmlEditor';
import PhoneInput from './PhoneInput';
import SelectLocationInput from '../locations/SelectLocationInput';
import SelectMarketInput from '../markets/SelectMarketInput';
import SelectProviderInput from '../providers/SelectProviderInput';
import SelectRegionInput from '../regions/SelectRegionInput';
import SelectTeamInput from '../teams/SelectTeamInput';
import SimpleDateField from './SimpleDateField';
import PayerPlanSelect from '../payer-plans/PayerPlanSelect';
import TimeField from './TimeField';

const FormInput = props => {
  const formHookProps = props?.formHookProps || {};
  const [blurred, setBlurred] = useState(false);
  const childProps = {
    ...props,
    error: (
      props?.error ||
      (!(props?.validate ? props.validate(props.value) : true) && blurred)
    ),
    onBlur: props?.controlled ? undefined : () => setBlurred(true),
  };
  if (props.hidden) return <></>;
  if (props.Component) return <props.Component {...childProps} />;
  switch (props.type) {
    case 'text':
      return <CustomTextField {...childProps} formHookProps={formHookProps} />;
    case 'textarea':
      return (
        <CustomTextField
          inputType='textarea'
          rows={5}
          multiline
          {...childProps}
        />
      );
    case 'number':
      return (
        <CustomNumberField
          type='number'
          {...childProps}
        />
      );
    case 'slider':
      return (
        <CustomSliderField {...childProps} formHookProps={formHookProps} />
      )
    case 'currency': 
      return <CurrencyField {...childProps} />;
    case 'options':
      return <CustomSelect {...childProps} />;
    case 'select':
      return <CustomSelect {...childProps} />;
    case 'boolean':
      return <BooleanField {...childProps} />;
    case 'choice':
      return <ChoiceField {...childProps} />;
    case 'email':
      return <CustomTextField type='email' {...childProps} />;
    case 'password':
      return <CustomTextField type='password' {...childProps} />;
    case 'phone':
      return <PhoneInput {...childProps} />;
    case 'html':
      return <HtmlEditor {...childProps} />;
    case 'date':
      return <DateField {...childProps} />;
    case 'simpleDate':
      return <SimpleDateField {...childProps} />;
    case 'daterange':
      return (
        <DateRangePicker
          label={props?.label}
          variant={props?.buttonVariant || 'text'}
          startDate={
            get(props, 'value.start')
              ? moment(props.value.start)
              : null
          }
          endDate={
            get(props, 'value.end')
              ? moment(props.value.end)
              : null
          }
          value={props.value}
          onChange={props.onChange}
          includeMonthOptions={props.includeMonthOptions}
        />
      );
    case 'daysOfWeek':
      return (
        <DayPicker
          {...childProps}
          onChange={(e, value) => childProps.onChange(e, value)}
        />
      );
    case 'time':
      return <TimeField {...childProps} />;
    case 'selectPatient':
      return (
        <Box style={{paddingTop: '0.5em'}}>
          <CustomInputLabel required={props?.required}>
            {props?.label || 'Selected Patient'}
          </CustomInputLabel>
          <FindPatientForm 
            allPatients={isBoolean(childProps.allPatients) ? childProps.allPatients : true} 
            {...childProps} 
            align='flex-start'
            color={props?.color || 'primary'}
            size='small'
            onSubmit={childProps.onChange} 
          />
        </Box>
      );
    case 'selectProvider':
      return (
        <SelectProviderInput {...childProps} />
      );
    case 'selectPayer':
      return (
        <FindPayerInput 
          {...childProps} 
          defaultPayerId={childProps?.defaultValue}
          onSubmit={childProps.onChange} 
        />
      );
    case 'selectLocation':
      return (
        <SelectLocationInput
          {...childProps}
          onChange={childProps.onChange}
        />
      );
    case 'selectCase':
      // requires props.patientId!
      return <FindCaseInput {...childProps} onSubmit={childProps.onChange} />;
    case 'selectPayerPlan':
      return <PayerPlanSelect {...childProps} onSubmit={childProps.onChange} />;
    case 'selectReferrer':
      return <FindReferrerInput {...childProps} onSubmit={childProps.onChange} />;
    case 'selectReferrerCompany':
      return <FindReferrerCompanyInput {...childProps} onSubmit={childProps.onChange} />;
    case 'selectRegion':
      return <SelectRegionInput {...childProps} onChange={childProps.onChange} />;
    case 'selectMarket':
      return <SelectMarketInput {...childProps} onChange={childProps.onChange} />;
    case 'selectTeam':
      return <SelectTeamInput {...childProps} onChange={childProps.onChange} />;
    case 'address':
      return (
        <AddressField 
          {...childProps} 
          onChange={address => childProps.onChange({target: {value: address}}, address)}
        />
      );
    case 'color':
      return (
        <ColorPicker
          {...childProps}
          onChange={(e, color) => childProps.onChange(e, color)}
        />
      );
    default:
      return <CustomTextField {...childProps} />;
  }
};

FormInput.propTypes = {
  type: PropTypes.string.isRequired,
};

export default FormInput;